import { ErrorObject } from 'ajv'
import { TwoBtnsContainer } from 'components/atoms/Layout'
import { loadingSpinnerPortal } from 'components/atoms/LoadingSpinner'
import GoBackHeader from 'components/organisms/GoBackHeader'
import { findObjectPathsByKeyValue } from 'find-object-paths'
import { useAppDispatch, useAppSelector } from 'hooks/store'
import useLoadingDelay from 'hooks/useLoadingDelay'
import { cloneDeep, debounce, get, isEqual, set, unset } from 'lodash'
import { createContext, FC, useEffect, useMemo, useRef, useState } from 'react'
import { Button } from 'react-bootstrap'
import { Redirect, useLocation } from 'react-router-dom'
import {
  saveUiTransformedModelToSessionStorage,
  setCurrentActiveGroup,
  setCurrentActiveQuestionId,
  setFormDataToCurrentGroupQuestion,
  setFormDataToCurrentQuestion,
  setQuestionCompleteFlagToCurrentGroupQuestion,
  setQuestionCompleteFlagToCurrentQuestion
} from 'reducers/activesection/activesectionSlice'
import {
  SAVE_QUESTIONNAIRE_SECTION_DATA,
  SaveQuestionnaireSectionDataResponse,
  SaveQuestionnaireSectionDataVariables
} from 'share/mutations/cases'

import { useMutation } from '@apollo/client'
import { toDataPath, ValidationMode } from '@jsonforms/core'
import { materialCells } from '@jsonforms/material-renderers'
import { JsonForms } from '@jsonforms/react'

import ProgressBarComponent from '../Components/ProgressBar'
import {
  formRenderers,
  Group,
  OperationType,
  Question,
  QUESTION_TYPE
} from '../constants'

import {
  findAllByKey,
  findNestedKeyPath,
  findPaths,
  getRandomString
} from '../utils'
import { ButtonsContainer, JsonformContainer } from './styles'
import { MatterClientPortalQuestionnaireSectionStatus } from 'share/domain/case'

interface VisitedSections {
  [sectionName: string]: boolean
}

interface IJsonFormCustomContext {
  cardActiveIndex: string
  omitEmptyDataHandler?: (path: string) => void
  removeNestedDataHandler?: (path: string) => void
  fetchNestedSchema?: (
    nextSchema: string,
    path: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    nextData: any,
    id: number,
    arrayIndex?: number,
    currentDataArrayPath?: string
  ) => void
  optionValueChangeHandler?: (value: string) => void
}

export const JsonFormCustomContext = createContext<IJsonFormCustomContext>({
  cardActiveIndex: '0',
  omitEmptyDataHandler: undefined,
  removeNestedDataHandler: undefined,
  fetchNestedSchema: undefined,
  optionValueChangeHandler: undefined
})

interface GroupData {
  [key: string]: {
    totalQuestions: number
    questionsCompleted: number[]
  }
}

const QuestionnaireForm: FC = () => {
  const dispatch = useAppDispatch()
  const location = useLocation()

  const activeCase = useAppSelector((state) => state.activeCase)
  const activeSection = useAppSelector((state) => state.activeSection)
  // eslint-disable-next-line
  const [schema, setSchema] = useState<any>({})
  // eslint-disable-next-line
  const [uiSchema, setUiSchema] = useState<any>({})
  // eslint-disable-next-line
  const [data, setData] = useState<any>({})

  const [dataSaveError, setDataSaveError] = useState(false)
  const currentActiveQuestionId = useAppSelector(
    (state) => state.activeSection.section?.currentActiveQuestionId
  )
  const activeGroup = useAppSelector(
    (state) => state.activeSection.section?.currentActiveGroup
  )

  const groupDataRef = useRef<GroupData[]>([])

  const [questionType, setQuestionType] = useState<string>(QUESTION_TYPE.SINGLE)
  const [operationType, setOperationType] = useState<OperationType>(
    OperationType.Empty
  )
  const [isChildQuestion, setIsChildQuestion] = useState<boolean>(false)

  // eslint-disable-next-line
  const [activeQuestion, setActiveQuestion] = useState<any>({})

  const [validationMode, setValidationMode] =
    useState<ValidationMode>('ValidateAndHide')

  const skipValidation = useRef(false)
  const [formErrors, setFormErrors] = useState<ErrorObject[]>()

  const [toSummaryPage, setToSummaryPage] = useState<boolean>(false)
  const [progress, setProgress] = useState<number>(0)
  const [toDonePage, setToDonePage] = useState(false)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [originalData, setOriginalData] = useState<{ [key: string]: any }>({})

  const [activeCardIndexes, setActiveCardIndexes] = useState<number[]>([])
  const [activeIndex, setActiveIndex] = useState<string>('0')
  const [isSchemaLoading, setIsSchemaLoading] = useState(false)
  const [isUISchemaLoading, setIsUISchemaLoading] = useState(false)

  const errorHeaderRef = useRef<HTMLDivElement>(null)

  const sectionStatusRef = useRef(activeSection.section?.sectionStatus ?? 0)

  const doneVisited = useRef<VisitedSections>(
    JSON.parse(sessionStorage.getItem('asections-done-visited') || '{}')
  )

  const [saveQuestionnaireSectionData, { loading }] = useMutation<
    SaveQuestionnaireSectionDataResponse,
    SaveQuestionnaireSectionDataVariables
  >(SAVE_QUESTIONNAIRE_SECTION_DATA)

  function getCaseId() {
    return activeCase?.matter?.id ? activeCase?.matter.id : 0
  }

  function getSectionId() {
    if (activeSection) {
      if (activeSection.section) {
        if (activeSection.section.id) {
          return activeSection.section.id
        }
      }
    }
    return 0
  }

  const getActiveQuestion = () => {
    // eslint-disable-next-line
    let questions: any[] =
      activeSection.section?.sectionUiTransformedModel.Questions || []
    if (questions && questions.length === 0) {
      const selectedGroup =
        activeSection.section?.sectionUiTransformedModel.Groups.find(
          (group: Group) => {
            return group.QuestionGroupId === activeGroup
          }
        )
      if (selectedGroup?.Questions?.length) {
        questions = selectedGroup.Questions
      }
    }

    return questions.find(
      (question) => question.QuestionId === currentActiveQuestionId
    )
  }

  const getSchemaForCurrentActiveQuestion = () => {
    if (!!activeQuestion && !!activeQuestion?.UnitForm?.Schema) {
      return activeQuestion.UnitForm.Schema
    }

    return {}
  }

  const getSchemaByParent = (parentSchemaName: string) => {
    if (activeQuestion) {
      if (activeQuestion?.UnitForm?.UISchema.schemaName === parentSchemaName) {
        return activeQuestion?.UnitForm?.Schema
      }
      if (activeQuestion?.SubQuestions) {
        for (const key in activeQuestion?.SubQuestions) {
          const value = activeQuestion?.SubQuestions[key]
          if (value.UnitForm.UISchema['schemaName'] === parentSchemaName) {
            return value.UnitForm.Schema
          }
        }
      }
    }
  }

  const getUISchemaByParent = (parentSchemaName: string) => {
    if (activeQuestion) {
      if (activeQuestion?.UnitForm?.UISchema.schemaName === parentSchemaName) {
        return activeQuestion?.UnitForm?.UISchema
      }
      if (activeQuestion?.SubQuestions) {
        for (const key in activeQuestion?.SubQuestions) {
          const value = activeQuestion?.SubQuestions[key]
          if (value.UnitForm.UISchema['schemaName'] === parentSchemaName) {
            return value.UnitForm.UISchema
          }
        }
      }
    }
  }

  const getUiSchemaForCurrentActiveQuestion = () => {
    if (!!activeQuestion && !!activeQuestion?.UnitForm?.UISchema) {
      return activeQuestion.UnitForm.UISchema
    }

    return {}
  }

  const getDataForCurrentActiveQuestion = () => {
    const activeQuestion = getActiveQuestion()
    if (!!activeQuestion && !!activeQuestion?.UnitForm?.Data) {
      return activeQuestion.UnitForm.Data
    }
    return { id: '123456' }
  }

  const setDataForCurrentActiveGroupQuestion = () => {
    const activeQuestion = getActiveQuestion()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let savedData: any
    if (!!activeQuestion && !!activeQuestion?.UnitForm?.Data) {
      savedData = activeQuestion.UnitForm.Data
    }

    if (Object.keys(savedData).length === 0) {
      const newData = { id: getRandomString(), parentId: 0 }
      setOperationType(OperationType.Load)
      setOriginalData(newData)
    } else {
      setOperationType(OperationType.Load)
      const clonedSavedData = cloneDeep(savedData)
      setOriginalData(clonedSavedData)
    }
  }

  useEffect(() => {
    if (currentActiveQuestionId && !!activeSection) {
      skipValidation.current = false
      setActiveQuestion(getActiveQuestion())
    }
  }, [currentActiveQuestionId])

  function getIndexByGroupId(group: {
    QuestionGroupId: number
    Questions: Question[]
  }): number {
    for (let i = 0; i < groupDataRef.current.length; i++) {
      if (groupDataRef.current[i][group.QuestionGroupId.toString()]) {
        return i
      }
    }
    return -1
  }

  function allQuestionsCompleted(): boolean {
    return groupDataRef.current.every((groupData) => {
      const keys = Object.keys(groupData)
      const totalQuestions = groupData[keys[0]].totalQuestions
      const questionsCompleted = groupData[keys[0]].questionsCompleted
      return totalQuestions === questionsCompleted.length
    })
  }

  useEffect(() => {
    const groups = activeSection.section?.sectionUiTransformedModel.Groups || []
    if (groups && groups.length) {
      groups.forEach(
        (group: { QuestionGroupId: number; Questions: Question[] }) => {
          const totQuestions = (
            group.Questions.filter((question) => {
              return !question.IsDisabled
            }) || []
          ).length

          const questionsCompleted = group.Questions.filter(
            (question) => question.UnitForm.QuestionCompleteFlag === true
          ).map((question) => question.QuestionId)

          const index = getIndexByGroupId(group)
          if (index === -1) {
            groupDataRef.current.push({
              [group.QuestionGroupId]: {
                totalQuestions: totQuestions,
                questionsCompleted: questionsCompleted
              }
            })
          } else {
            const questionGroup =
              groupDataRef.current[index][group.QuestionGroupId]
            questionGroup.totalQuestions = totQuestions
            questionGroup.questionsCompleted = questionsCompleted
            groupDataRef.current[index][group.QuestionGroupId] = questionGroup
          }
        }
      )
      setProgressForGroup()
    }
  }, [])

  useEffect(() => {
    setIsSchemaLoading(false)
  }, [schema])

  function scrollToErrorHeader() {
    if (
      errorHeaderRef.current !== null &&
      errorHeaderRef?.current?.scrollIntoView !== null
    ) {
      errorHeaderRef?.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }

  function updateGroupDataForCurrentActiveQuestion(
    questionCompleteFlag: boolean
  ): void {
    const groups = activeSection.section?.sectionUiTransformedModel.Groups || []
    if (groups?.length) {
      const currentActiveGroup = groups.find(
        (group: { QuestionGroupId: number }) => {
          return group.QuestionGroupId === activeGroup
        }
      )

      if (!currentActiveGroup) return

      const index = getIndexByGroupId(currentActiveGroup)
      if (index === -1) return

      const group = groupDataRef.current[index]
      const obj = Object.keys(group)[0]

      const questionsCompleted = group[obj]['questionsCompleted']
      const questionIndex = questionsCompleted.indexOf(
        currentActiveQuestionId as number
      )
      if (questionCompleteFlag && questionIndex === -1) {
        questionsCompleted.push(currentActiveQuestionId as number)
      } else if (!questionCompleteFlag && questionIndex !== -1) {
        questionsCompleted.splice(questionIndex, 1)
      }
    }
  }

  useEffect(() => {
    let timeoutId: number

    if (operationType === OperationType.Load && !data.id) {
      setData(originalData)
    }

    // When user clicks on the save button
    if (operationType === OperationType.Save) {
      const currentFormStatus = getFormCompleteStatus()
      ;(async function () {
        const saveDataResult = await saveNestedQuestionData(false)
        if (saveDataResult) {
          if (
            currentFormStatus &&
            !currentFormStatus.isCompleted &&
            !skipValidation.current
          ) {
            setValidationMode('ValidateAndShow')
            scrollToErrorHeader()
          } else {
            setValidationMode('ValidateAndHide')
            const path = findPaths(
              originalData,
              'id',
              data['parentId']
            ) as string

            if (path?.length && data['parentId']) {
              const newActiveCardIndexes = [...activeCardIndexes]

              if (newActiveCardIndexes?.length) {
                const lastIndex = newActiveCardIndexes.pop()
                setActiveCardIndexes(newActiveCardIndexes)
                if (typeof lastIndex === 'number' && !isNaN(lastIndex)) {
                  setActiveIndex(lastIndex.toString())
                }
              }
              const pathArr = path.split('.')
              if (pathArr.length < 4) {
                setData(originalData)
                setSchemaAndUISchema('BY_PARENT')
              } else {
                pathArr.splice(pathArr.length - 2, 2)

                const joinedPath = pathArr.join('.')

                const previousData = get(originalData, joinedPath)

                setData(previousData)
                setSchemaAndUISchema('BY_PARENT')
              }
            } else {
              setData(originalData)
              setSchemaAndUISchema('BY_PARENT')
            }
          }
        }
      })()
    }

    // When user clicks on the continue button on parent question
    if (operationType === OperationType.Continue) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const currentFormStatus = getFormCompleteStatus()

      const saveNestedData = async () => {
        const saveDataResult = await saveNestedQuestionData(true)

        if (saveDataResult) {
          if (
            currentFormStatus &&
            !currentFormStatus.isCompleted &&
            !skipValidation.current
          ) {
            updateGroupDataForCurrentActiveQuestion(false)
            setProgressForGroup()

            setValidationMode('ValidateAndShow')
            scrollToErrorHeader()
          } else {
            updateGroupDataForCurrentActiveQuestion(true)
            setProgressForGroup()

            setActiveCardIndexes([])
            setActiveIndex('0')
            dispatch(setFormDataToCurrentGroupQuestion(originalData))
            dispatch(setCurrentActiveQuestionId(null))
            setData({})
            setValidationMode('ValidateAndHide')

            const areQuestionsCompleted = allQuestionsCompleted()

            if (
              activeSection.section?.sectionStatus !==
                MatterClientPortalQuestionnaireSectionStatus.Submitted &&
              activeSection.section?.sectionStatus !==
                MatterClientPortalQuestionnaireSectionStatus.Imported &&
              areQuestionsCompleted &&
              doneVisited.current &&
              !doneVisited.current[activeSection.section?.sectionName as string]
            ) {
              setToDonePage(true)
            } else {
              const nextAvailableQuestionId = getNextAvailableQuestionInGroup()
              if (nextAvailableQuestionId === -1) {
                dispatch(setCurrentActiveQuestionId(null))
                setToSummaryPage(true)
              } else {
                dispatch(setCurrentActiveQuestionId(nextAvailableQuestionId))
              }
            }
          }
        }
      }
      saveNestedData()
    }

    if (operationType === OperationType.Back) {
      ;(async function () {
        const currentFormStatus = getFormCompleteStatus()

        if (currentFormStatus && !currentFormStatus.isCompleted) {
          updateGroupDataForCurrentActiveQuestion(false)
        } else {
          updateGroupDataForCurrentActiveQuestion(true)
        }
        setProgressForGroup()
        setValidationMode('ValidateAndHide')

        const path = findPaths(originalData, 'id', data['parentId']) as string

        if (path && path.length && data['parentId']) {
          const pathArr = path.split('.')
          if (pathArr.length < 4) {
            const newActiveCardIndexes = [...activeCardIndexes]

            if (newActiveCardIndexes && newActiveCardIndexes.length) {
              const lastIndex = newActiveCardIndexes.pop()
              setActiveCardIndexes(newActiveCardIndexes)
              if (typeof lastIndex === 'number' && !isNaN(lastIndex)) {
                setActiveIndex(lastIndex.toString())
              }
            }
            setData(originalData)
            setSchemaAndUISchema('BY_PARENT')
          } else {
            const newActiveCardIndexes = [...activeCardIndexes]

            if (newActiveCardIndexes && newActiveCardIndexes.length) {
              const lastIndex = newActiveCardIndexes.pop()

              setActiveCardIndexes(newActiveCardIndexes)
              if (typeof lastIndex === 'number' && !isNaN(lastIndex)) {
                setActiveIndex(lastIndex.toString())
              }
            }
            pathArr.splice(pathArr.length - 2, 2)
            const joinedPath = pathArr.join('.')
            const previousData = get(originalData, joinedPath)
            setData(previousData)
            setSchemaAndUISchema('BY_PARENT')
          }
        } else {
          setData(originalData)
          setSchemaAndUISchema('BY_PARENT')
        }
      })()
    }

    if (operationType === OperationType.SaveAndFinish) {
      const currentFormStatus = getFormCompleteStatus()
      const questionCompleteFlag =
        currentFormStatus && !currentFormStatus.isCompleted ? false : true
      if (currentFormStatus && !currentFormStatus.isCompleted) {
        updateGroupDataForCurrentActiveQuestion(questionCompleteFlag)
      }
      const saveNestedData = async () => {
        if (timeoutId) {
          clearTimeout(timeoutId)
        }

        const saveDataResult = await saveNestedQuestionData(true)

        if (saveDataResult) {
          dispatch(setFormDataToCurrentGroupQuestion(originalData))

          setValidationMode('ValidateAndHide')
          dispatch(setCurrentActiveQuestionId(null))
          setToSummaryPage(true)
        }
      }
      saveNestedData()
    }

    if (operationType === OperationType.BackAndSave) {
      const currentFormStatus = getFormCompleteStatus()
      const questionCompleteFlag =
        currentFormStatus && !currentFormStatus.isCompleted ? false : true
      if (currentFormStatus && !currentFormStatus.isCompleted) {
        updateGroupDataForCurrentActiveQuestion(questionCompleteFlag)
      }
      const saveNestedData = async () => {
        if (timeoutId) {
          clearTimeout(timeoutId)
        }
        const saveDataResult = await saveNestedQuestionData(true)
        if (saveDataResult) {
          setData({})
          dispatch(setFormDataToCurrentGroupQuestion(originalData))
          setValidationMode('ValidateAndHide')
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const navigationQuestionId = (location?.state as any)
            ?.navigationQuestionId

          if (
            navigationQuestionId &&
            currentActiveQuestionId === navigationQuestionId
          ) {
            dispatch(setCurrentActiveQuestionId(null))
            setToSummaryPage(true)
            return
          }

          const previousAvailableQuestionId = getPreviousQuestion()
          if (previousAvailableQuestionId === -1) {
            console.log('No question available')
            dispatch(setCurrentActiveQuestionId(null))
            setToSummaryPage(true)
          } else {
            dispatch(setCurrentActiveQuestionId(previousAvailableQuestionId))
          }
        }
      }
      saveNestedData()
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [originalData])

  useEffect(() => {
    if (uiSchema) {
      if (uiSchema.parentSchemaName && uiSchema.parentSchemaName !== '') {
        setIsChildQuestion(true)
      } else {
        setIsChildQuestion(false)
      }
      setIsUISchemaLoading(false)
    }
  }, [uiSchema])

  const getProgressPercent = (isLastQuestionCompleted?: boolean): number => {
    const questions = activeSection.section?.sectionUiTransformedModel.Questions
    let completedQuestions = 0

    for (let i = 0; i < questions.length; i++) {
      if (questions[i].UnitForm['QuestionCompleteFlag']) {
        ++completedQuestions
      } else {
        if (i === questions.length - 1 && isLastQuestionCompleted) {
          ++completedQuestions
        }
      }
    }
    const progress = (completedQuestions / questions.length) * 100
    return progress
  }

  const getGroupProgressPercent = (): number => {
    let totalQuestionsInSection = 0
    let totalCompletedQuestionInSection = 0

    if (groupDataRef && groupDataRef.current) {
      groupDataRef.current.forEach((groupData) => {
        const keys = Object.keys(groupData)
        totalQuestionsInSection =
          totalQuestionsInSection + groupData[keys[0]].totalQuestions
        totalCompletedQuestionInSection =
          totalCompletedQuestionInSection +
          groupData[keys[0]].questionsCompleted.length
      })
      let progress = 0
      progress =
        totalQuestionsInSection > 0
          ? (totalCompletedQuestionInSection / totalQuestionsInSection) * 100
          : 0
      return progress
    }
    return 0
  }

  function setProgressPercent() {
    setProgress(getProgressPercent())
  }

  function setProgressForGroup() {
    setProgress(getGroupProgressPercent())
  }

  function setSchemaAndUISchema(type: string, nextSchema?: string) {
    setIsSchemaLoading(true)
    setIsUISchemaLoading(true)
    let unitForm
    switch (type) {
      case 'CURRENT_ACTIVE_QUESTION':
        setSchema(getSchemaForCurrentActiveQuestion())
        setUiSchema(getUiSchemaForCurrentActiveQuestion())
        break
      case 'BY_PARENT':
        setSchema(getSchemaByParent(uiSchema.parentSchemaName))
        setUiSchema(getUISchemaByParent(uiSchema.parentSchemaName))
        break
      default:
        if (nextSchema) {
          unitForm = activeQuestion.SubQuestions[nextSchema]['UnitForm']
          setSchema(unitForm.Schema)
          setUiSchema(unitForm.UISchema)
        }
    }
  }

  useEffect(() => {
    if (activeQuestion) {
      const isNestedQuestion =
        activeQuestion.SubQuestions &&
        Object.keys(activeQuestion.SubQuestions).length !== 0
      if (activeGroup && activeQuestion) {
        isNestedQuestion
          ? setQuestionType(QUESTION_TYPE.GROUP_NESTED)
          : setQuestionType(QUESTION_TYPE.GROUP_SINGLE)
      } else {
        isNestedQuestion
          ? setQuestionType(QUESTION_TYPE.SINGLE_NESTED)
          : setQuestionType(QUESTION_TYPE.SINGLE)
        setProgressPercent()
      }

      setSchemaAndUISchema('CURRENT_ACTIVE_QUESTION')

      if (!isNestedQuestion) {
        let activeQuestionData = getDataForCurrentActiveQuestion()
        if (
          Object.keys(activeQuestionData).length === 0 ||
          !activeQuestionData['id']
        ) {
          activeQuestionData = { ...activeQuestionData, id: getRandomString() }
        }
        setData(activeQuestionData)
      } else {
        setDataForCurrentActiveGroupQuestion()
      }
    }
  }, [activeQuestion])

  const getQuestionKeyForCurrentActiveQuestion = () => {
    if (activeQuestion) {
      return activeQuestion.Value
    }

    return ''
  }

  async function saveQuestionnaireSectionDataHelper(
    caseId: number,
    sectionId: number,
    questionKey: string,
    data: string | null,
    questionCompleteFlag: boolean
  ): Promise<boolean> {
    let result = false
    try {
      const input = {
        caseId: caseId,
        sectionId: sectionId,
        questionKey: questionKey,
        data: data,
        questionCompleteFlag: questionCompleteFlag
      }

      const saveResult = await saveQuestionnaireSectionData({
        variables: { request: input }
      })
      if (saveResult.data?.cases.saveQuestionnaireSectionData.success) {
        result = true
      }
    } catch (e) {
      result = false
    }

    return result
  }

  async function saveSectionDataClick(
    caseId: number,
    sectionId: number,
    questionKey: string,
    data: string | null,
    questionCompleteFlag: boolean
  ) {
    const saveDataResult = await saveQuestionnaireSectionDataHelper(
      caseId,
      sectionId,
      questionKey,
      data,
      questionCompleteFlag
    )
    if (saveDataResult) {
      console.log('saved data successfully to db')
      // update session storage with saved data
      saveUiTransformedModelToSessionStorage(activeSection)
      return true
    } else {
      console.log('error saving data')
      return false
    }
  }

  const getNextAvailableQuestion = () => {
    let currentQuestionIndex = -1
    const questions =
      activeSection.section?.sectionUiTransformedModel.Questions || []

    currentQuestionIndex = questions.findIndex(
      // eslint-disable-next-line
      (question: any) => currentActiveQuestionId === question.QuestionId
    )

    if (currentQuestionIndex === questions.length - 1) {
      return -1
    }

    return questions[currentQuestionIndex + 1].QuestionId
  }

  const getNextAvailableQuestionInNextGroup = (selectedGroupIndex: number) => {
    const groups = activeSection.section?.sectionUiTransformedModel.Groups || []

    let nextAvailableQuestion: Question | undefined
    const nextAvailableGroup = groups
      .slice(selectedGroupIndex + 1)
      .find((group: Group) => {
        nextAvailableQuestion = group.Questions.find(
          (question: Question) => !question.IsDisabled
        )
        return group.Questions.length > 0 && nextAvailableQuestion
      })
    if (nextAvailableGroup && nextAvailableQuestion) {
      const nextGroupId = nextAvailableGroup.QuestionGroupId
      dispatch(setCurrentActiveGroup(nextGroupId))
      return nextAvailableQuestion.QuestionId
    }
    return -1
  }

  const getNextAvailableQuestionInGroup = () => {
    let currentQuestionIndex = -1
    const groups = activeSection.section?.sectionUiTransformedModel.Groups || []
    if (groups && groups.length) {
      const selectedGroupIndex = groups.findIndex(
        (group: { QuestionGroupId: number | null | undefined }) => {
          return group.QuestionGroupId === activeGroup
        }
      )
      if (selectedGroupIndex != -1) {
        const selectedGroup: Group = groups[selectedGroupIndex]
        if (selectedGroup && selectedGroup.Questions?.length) {
          const questions = selectedGroup.Questions
          currentQuestionIndex = questions.findIndex(
            (question: Question) =>
              currentActiveQuestionId === question.QuestionId
          )

          if (currentQuestionIndex === questions.length - 1) {
            if (selectedGroupIndex === groups.length - 1) {
              return -1
            } else {
              return getNextAvailableQuestionInNextGroup(selectedGroupIndex)
            }
          } else {
            const nextQuestions = questions.slice(currentQuestionIndex + 1)
            const nextQuestion = nextQuestions.find(
              (question) => !question.IsDisabled
            )
            if (!nextQuestion) {
              return getNextAvailableQuestionInNextGroup(selectedGroupIndex)
            } else {
              return nextQuestion.QuestionId
            }
          }
        }
        return -1
      }
      return -1
    }
    return -1
  }

  const getPreviousAvailableQuestion = (): number => {
    let currentQuestionIndex = -1
    const questions =
      activeSection.section?.sectionUiTransformedModel.Questions || []

    currentQuestionIndex = questions.findIndex(
      // eslint-disable-next-line
      (question: any) => currentActiveQuestionId === question.QuestionId
    )

    if (currentQuestionIndex === 0) {
      return -1
    }

    return questions[currentQuestionIndex - 1].QuestionId
  }

  const getPreviousAvailableQuestionInPreviousGroup = (
    selectedGroupIndex: number
  ) => {
    const groups = activeSection.section?.sectionUiTransformedModel.Groups || []

    let previousAvailableQuestion: Question | undefined
    const previousAvailableGroup = groups
      .slice(0, selectedGroupIndex)
      .reverse()
      .find((group: Group) => {
        previousAvailableQuestion = group.Questions.slice()
          .reverse()
          .find((question: Question) => !question.IsDisabled)
        return group.Questions.length > 0 && previousAvailableQuestion
      })
    if (previousAvailableGroup && previousAvailableQuestion) {
      const previousGroupId = previousAvailableGroup.QuestionGroupId
      dispatch(setCurrentActiveGroup(previousGroupId))
      return previousAvailableQuestion.QuestionId
    }
    return -1
  }

  const getPreviousAvailableQuestionFromGroup = (): number => {
    let currentQuestionIndex = -1
    const groups: Group[] =
      activeSection.section?.sectionUiTransformedModel.Groups || []

    if (groups && groups.length) {
      const selectedGroupIndex = groups.findIndex((group) => {
        return group.QuestionGroupId === activeGroup
      })
      const selectedGroup = groups.find((group) => {
        return group.QuestionGroupId === activeGroup
      })
      if (selectedGroup && selectedGroup.Questions?.length) {
        const questions = selectedGroup.Questions
        currentQuestionIndex = questions.findIndex(
          (question: Question) =>
            currentActiveQuestionId === question.QuestionId
        )

        if (currentQuestionIndex === 0) {
          if (selectedGroupIndex === 0) {
            return -1
          } else {
            return getPreviousAvailableQuestionInPreviousGroup(
              selectedGroupIndex
            )
          }
        } else {
          const previousQuestions = questions
            .slice(0, currentQuestionIndex)
            .reverse()
          const previousQuestion = previousQuestions.find(
            (question) => !question.IsDisabled
          )
          if (!previousQuestion) {
            return getPreviousAvailableQuestionInPreviousGroup(
              selectedGroupIndex
            )
          } else {
            return previousQuestion.QuestionId
          }
        }
      }
      return -1
    }
    return -1
  }

  const getPreviousQuestion = (): number => {
    if (
      questionType === QUESTION_TYPE.GROUP_NESTED ||
      questionType === QUESTION_TYPE.GROUP_SINGLE
    ) {
      return getPreviousAvailableQuestionFromGroup()
    } else {
      return getPreviousAvailableQuestion()
    }
  }

  const getRequiredErrors = () => {
    if (!!formErrors && formErrors.length) {
      return formErrors.filter((error: ErrorObject) => {
        return (
          error.keyword === 'required' ||
          error.keyword === 'minLength' ||
          error.message === 'should NOT be valid'
        )
      })
    }
    return []
  }

  const getQuestionCompleteFlag = (): boolean => {
    // eslint-disable-next-line
    let requiredErrors: any[] = getRequiredErrors()

    return !!requiredErrors && requiredErrors.length ? false : true
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  type JSONObject = { [key: string]: any }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function removeNullObjects(obj: any): any {
    if (typeof obj !== 'object' || obj === null) {
      return obj
    }

    if (Array.isArray(obj)) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return obj.map(removeNullObjects).filter((item: any) => item !== null)
    }

    const newObj: JSONObject = {}
    Object.entries(obj).forEach(([key, value]) => {
      const newValue = removeNullObjects(value)
      if (newValue !== null) {
        newObj[key] = newValue
      }
    })
    return Object.keys(newObj).length > 0 ? newObj : null
  }

  const saveSectionData = async () => {
    if (
      activeSection.section?.sectionStatus ===
        MatterClientPortalQuestionnaireSectionStatus.Submitted ||
      activeSection.section?.sectionStatus ===
        MatterClientPortalQuestionnaireSectionStatus.Imported
    ) {
      return Promise.resolve(true)
    }

    const questionKey = getQuestionKeyForCurrentActiveQuestion()
    let questionCompleteFlag = getQuestionCompleteFlag()

    if (skipValidation.current) {
      questionCompleteFlag = true
    }

    const nonNullJsonData = removeNullObjects(data)

    const saveDataResult = await saveSectionDataClick(
      getCaseId(),
      getSectionId(),
      questionKey,
      JSON.stringify(nonNullJsonData),
      questionCompleteFlag
    )

    if (!saveDataResult) {
      setDataSaveError(true)
      return saveDataResult
    }

    if (questionType === QUESTION_TYPE.GROUP_SINGLE) {
      dispatch(
        setQuestionCompleteFlagToCurrentGroupQuestion(questionCompleteFlag)
      )
    } else {
      dispatch(setQuestionCompleteFlagToCurrentQuestion(questionCompleteFlag))
    }

    return saveDataResult
  }

  const saveNestedQuestionData = async (isParentQuestion: boolean) => {
    if (
      activeSection.section?.sectionStatus ===
        MatterClientPortalQuestionnaireSectionStatus.Submitted ||
      activeSection.section?.sectionStatus ===
        MatterClientPortalQuestionnaireSectionStatus.Imported
    ) {
      return Promise.resolve(true)
    }

    const questionKey = getQuestionKeyForCurrentActiveQuestion()
    let questionCompleteFlag: boolean = isParentQuestion
      ? getFormCompleteStatus().isCompleted
      : false

    if (skipValidation.current) {
      questionCompleteFlag = true
    }

    const nonNullJsonData = removeNullObjects(originalData)

    const saveDataResult = await saveSectionDataClick(
      getCaseId(),
      getSectionId(),
      questionKey,
      JSON.stringify(nonNullJsonData),
      questionCompleteFlag
    )

    if (!saveDataResult) {
      setDataSaveError(true)
      return saveDataResult
    }

    dispatch(
      setQuestionCompleteFlagToCurrentGroupQuestion(questionCompleteFlag)
    )

    return saveDataResult
  }

  const continueClick = debounce(async () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const requiredErrors: any[] = getRequiredErrors()
    let saveDataResult = true

    // Bypass save operation for submitted sections.
    if (
      activeSection.section?.sectionStatus !==
        MatterClientPortalQuestionnaireSectionStatus.Submitted &&
      activeSection.section?.sectionStatus !==
        MatterClientPortalQuestionnaireSectionStatus.Imported
    ) {
      saveDataResult = await saveSectionData()
    }

    if (saveDataResult) {
      if (questionType === QUESTION_TYPE.GROUP_SINGLE) {
        if (requiredErrors?.length && !skipValidation.current) {
          setValidationMode('ValidateAndShow')
          scrollToErrorHeader()
        } else {
          setActiveCardIndexes([])
          setActiveIndex('0')
          dispatch(setFormDataToCurrentGroupQuestion(data))
          dispatch(setCurrentActiveQuestionId(null))
          setOperationType(OperationType.Empty)
          setOriginalData({})
          setData({})
          setValidationMode('ValidateAndHide')

          updateGroupDataForCurrentActiveQuestion(true)
          setProgressForGroup()

          const areQuestionsCompleted = allQuestionsCompleted()

          if (
            activeSection.section?.sectionStatus !==
              MatterClientPortalQuestionnaireSectionStatus.Submitted &&
            activeSection.section?.sectionStatus !==
              MatterClientPortalQuestionnaireSectionStatus.Imported &&
            areQuestionsCompleted &&
            doneVisited.current &&
            !doneVisited.current[activeSection.section?.sectionName as string]
          ) {
            setToDonePage(true)
          } else {
            const nextAvailableQuestionId = getNextAvailableQuestionInGroup()
            if (nextAvailableQuestionId === -1) {
              setToSummaryPage(true)
            } else {
              dispatch(setCurrentActiveQuestionId(nextAvailableQuestionId))
            }
          }
        }
      } else {
        dispatch(setFormDataToCurrentQuestion(data))

        const currentFormStatus = getFormCompleteStatus()

        if (
          QUESTION_TYPE.SINGLE_NESTED &&
          !currentFormStatus?.isCompleted &&
          !skipValidation.current
        ) {
          setValidationMode('ValidateAndShow')
          scrollToErrorHeader()
        } else {
          if (requiredErrors?.length && !skipValidation.current) {
            setValidationMode('ValidateAndShow')
            scrollToErrorHeader()
          } else {
            setOperationType(OperationType.Empty)
            setData({})
            setOriginalData({})
            setActiveCardIndexes([])
            setActiveIndex('0')

            dispatch(setCurrentActiveQuestionId(null))
            setValidationMode('ValidateAndHide')
            const nextAvailableQuestionId = getNextAvailableQuestion()

            if (nextAvailableQuestionId === -1) {
              if (
                getProgressPercent(true) > 99 &&
                activeSection.section?.sectionStatus !==
                  MatterClientPortalQuestionnaireSectionStatus.Submitted &&
                activeSection.section?.sectionStatus !==
                  MatterClientPortalQuestionnaireSectionStatus.Imported
              ) {
                console.log('All questions completed')
                setProgress(100)
                setToDonePage(true)
              } else {
                dispatch(setCurrentActiveQuestionId(null))
                setToSummaryPage(true)
              }
            } else {
              dispatch(setCurrentActiveQuestionId(nextAvailableQuestionId))
            }
          }
        }
      }
    }
  }, 400)

  const nestedContinueClick = debounce(() => {
    setOperationType(OperationType.Continue)

    const cloned = cloneDeep(data)
    setOriginalData(cloned)
  }, 400)

  const saveAndFinishLater = async () => {
    if (questionType === QUESTION_TYPE.GROUP_NESTED) {
      setOperationType(OperationType.SaveAndFinish)
      const clonedData = cloneDeep(data)
      setOriginalData(clonedData)
    } else {
      const saveDataResult = await saveSectionData()
      if (saveDataResult) {
        if (questionType === QUESTION_TYPE.GROUP_SINGLE) {
          dispatch(setFormDataToCurrentGroupQuestion(data))
        } else {
          dispatch(setFormDataToCurrentQuestion(data))
        }
        setValidationMode('ValidateAndHide')
        dispatch(setCurrentActiveQuestionId(null))
        setToSummaryPage(true)
      }
    }
  }

  function goBackHeaderOnClick() {
    dispatch(setCurrentActiveQuestionId(null))
    setToSummaryPage(true)
  }

  function getFormCompleteStatus() {
    const requiredErrorsForCurrentForm = getRequiredErrors()

    const currentObjectPath = findPaths(
      originalData,
      'id',
      data['id']
    ) as string

    if (!currentObjectPath) {
      return { isCompleted: true }
    }
    const replacedPath = currentObjectPath.replace('.id', '')

    const splittedPath = replacedPath.split('.')
    const navigation_name =
      'navigation_' + splittedPath[splittedPath.length - 1]

    const navigation_path = findNestedKeyPath(
      originalData,
      data['parentId'],
      navigation_name
    )?.join('.')

    const navigationVal = get(originalData, navigation_path as string)

    if (requiredErrorsForCurrentForm && requiredErrorsForCurrentForm.length) {
      return { ...navigationVal, isCompleted: false }
    } else {
      const navigationObjects = findAllByKey(data, 'navigation')
      const foundNavigationError = navigationObjects.find(
        (navigationObject: { isCompleted: boolean }) =>
          navigationObject.isCompleted === false
      )
      if (foundNavigationError) {
        return { ...navigationVal, isCompleted: false }
      } else {
        return { ...navigationVal, isCompleted: true }
      }
    }
  }

  const onChildQuestionBackClick = async () => {
    const currentObjectPath = findPaths(
      originalData,
      'id',
      data['id']
    ) as string

    const replacedPath = currentObjectPath.replace('.id', '')

    const splittedPath = replacedPath.split('.')
    const navigation_name =
      'navigation_' + splittedPath[splittedPath.length - 1]

    setOperationType(OperationType.Back)
    setValidationMode('ValidateAndHide')

    const clonedOriginalData = cloneDeep(originalData)
    let navigation_path = ''

    if (!data['parentId']) {
      splittedPath[splittedPath.length - 1] = navigation_name
      navigation_path = splittedPath.join('.')
    } else {
      navigation_path = findNestedKeyPath(
        clonedOriginalData,
        data['parentId'],
        navigation_name
      )?.join('.') as string
    }

    const newNavigationVal = getFormCompleteStatus()

    set(clonedOriginalData, navigation_path, newNavigationVal)
    set(clonedOriginalData, replacedPath, data)
    setOriginalData(clonedOriginalData)
  }

  const onChildQuestionSaveClick = async () => {
    setOperationType(OperationType.Save)

    const currentObjectPath = findPaths(
      originalData,
      'id',
      data['id']
    ) as string

    const replacedPath = currentObjectPath.replace('.id', '')

    const splittedPath = replacedPath.split('.')
    const navigation_name =
      'navigation_' + splittedPath[splittedPath.length - 1]
    let navigation_path = ''

    const clonedOriginalData = cloneDeep(originalData)

    if (!data['parentId']) {
      splittedPath[splittedPath.length - 1] = navigation_name
      navigation_path = splittedPath.join('.')
    } else {
      navigation_path = findNestedKeyPath(
        clonedOriginalData,
        data['parentId'],
        navigation_name
      )?.join('.') as string
    }
    const newNavigationVal = getFormCompleteStatus()

    set(clonedOriginalData, navigation_path, newNavigationVal)
    set(clonedOriginalData, replacedPath, data)
    setOriginalData(clonedOriginalData)
  }

  const backClick = async () => {
    if (questionType === QUESTION_TYPE.GROUP_NESTED) {
      setOperationType(OperationType.BackAndSave)
      const clonedData = cloneDeep(data)
      setOriginalData(clonedData)
    } else {
      const saveDataResult = await saveSectionData()
      if (saveDataResult) {
        if (questionType === QUESTION_TYPE.GROUP_SINGLE) {
          dispatch(setFormDataToCurrentGroupQuestion(data))
        } else {
          dispatch(setFormDataToCurrentQuestion(data))
        }

        setData({})

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const navigationQuestionId = (location?.state as any)
          ?.navigationQuestionId

        if (
          navigationQuestionId &&
          currentActiveQuestionId === navigationQuestionId
        ) {
          dispatch(setCurrentActiveQuestionId(null))
          setToSummaryPage(true)
          return
        }

        const previousAvailableQuestionId = getPreviousQuestion()
        if (previousAvailableQuestionId === -1) {
          console.log('No question available')
          dispatch(setCurrentActiveQuestionId(null))
          setToSummaryPage(true)
        } else {
          dispatch(setCurrentActiveQuestionId(previousAvailableQuestionId))
        }
      }
    }
  }

  const loadingDelay = useLoadingDelay(loading)

  const omitEmptyDataHandler = (path: string) => {
    const timeoutId = setTimeout(() => {
      const clonedData = cloneDeep(data)
      unset(clonedData, path)
      setData(clonedData)
    }, 300)

    return () => {
      clearTimeout(timeoutId)
    }
  }

  const removeNestedDataHandler = (path: string) => {
    const timeoutId = setTimeout(() => {
      const clonedData = cloneDeep(data)
      unset(clonedData, path)
      setData(clonedData)
    }, 0)

    return () => {
      clearTimeout(timeoutId)
    }
  }

  function getObjectWithOptions() {
    if (Array.isArray(uiSchema.elements)) {
      for (const element of uiSchema.elements) {
        if (element?.options && element?.options?.detail) {
          return element
        }
      }
    }
    return null // Return null if no object with options is found
  }

  function getBreadcrumbTitleForChildQuestion(
    currentDataArrayPath: string,
    arrayIndex: number
  ): string | null {
    const uiSchemaElementWithOptions = getObjectWithOptions()

    if (!uiSchemaElementWithOptions) {
      if (currentDataArrayPath.includes('debtor1')) {
        return 'Debtor 1'
      }
      if (currentDataArrayPath.includes('debtor2')) {
        return 'Debtor 2'
      }
      return null
    }
    const options = uiSchemaElementWithOptions?.options
    const detail = options?.detail
    const firstElementScope = detail?.elements[0]?.scope

    const firstElementDataPath = toDataPath(firstElementScope)

    const targetObject = get(data, currentDataArrayPath as string)

    const title = get(targetObject, firstElementDataPath)
    if (!title) {
      return uiSchemaElementWithOptions.header + ' ' + (arrayIndex + 1)
    }
    return title
  }

  const fetchNestedSchema = (
    nextSchema: string,
    nextDataPath: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    nextData: any,
    id: number,
    arrayIndex?: number,
    currentDataArrayPath?: string
  ) => {
    setValidationMode('ValidateAndHide')
    setOperationType(OperationType.Next)

    const newpath = findObjectPathsByKeyValue(
      originalData,
      'id',
      data['id']
    ) as string

    let breadCrumbTitle = getBreadcrumbTitleForChildQuestion(
      currentDataArrayPath as string,
      arrayIndex as number
    )

    if (breadCrumbTitle === undefined || breadCrumbTitle === null) {
      breadCrumbTitle =
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (uiSchema as any).navigationHeader + ((arrayIndex as number) + 1)
    }

    const newData =
      nextData && nextData['id']
        ? { ...nextData, navigation: breadCrumbTitle }
        : {
            id: getRandomString(),
            parentId: id,
            navigation: breadCrumbTitle
          }

    const clonedData = cloneDeep(data)
    set(clonedData, nextDataPath, newData)

    if (newpath) {
      const replacedPath = newpath.replace('.id', '')

      if (newpath === 'id') {
        setOriginalData(clonedData)
      } else {
        const clonedOriginalData = cloneDeep(originalData)
        set(clonedOriginalData, replacedPath, clonedData)
        setOriginalData(clonedOriginalData)
      }
    } else {
      setOriginalData(clonedData)
    }

    setData(newData)

    if (typeof arrayIndex === 'number' && !isNaN(arrayIndex)) {
      setActiveCardIndexes([...activeCardIndexes, arrayIndex])
      setActiveIndex('0')
    }

    setSchemaAndUISchema('', nextSchema)
  }

  const optionValueChangeHandler = (value: string) => {
    if (value === 'SYS_USER_NEED_HELP') {
      setValidationMode('NoValidation')
      skipValidation.current = true
    } else {
      setValidationMode('ValidateAndHide')
      skipValidation.current = false
    }
  }

  const contextValue = useMemo(() => {
    return {
      omitEmptyDataHandler,
      removeNestedDataHandler,
      fetchNestedSchema,
      cardActiveIndex: activeIndex,
      optionValueChangeHandler
    }
  }, [
    omitEmptyDataHandler,
    removeNestedDataHandler,
    fetchNestedSchema,
    activeIndex,
    optionValueChangeHandler
  ])

  const handleChangeInForm = debounce((formData, errors) => {
    setFormErrors(errors)
    if (!isEqual(data, formData) && formData.id) {
      setData(formData)
    }
  }, 50)

  const handleFormDataChange = (
    formData: Record<string, unknown>,
    errors: ErrorObject[]
  ) => {
    handleChangeInForm(formData, errors)
  }

  if (toSummaryPage) {
    return (
      <Redirect
        to={`/dashboard/questionnaires/sectionsummary/${getSectionId()}`}
      />
    )
  }

  if (toDonePage) {
    return <Redirect to={`/dashboard/questionnaires/done`} />
  }

  return (
    <>
      {(loadingDelay || isSchemaLoading || isUISchemaLoading) &&
        loadingSpinnerPortal()}

      <GoBackHeader
        text={activeSection.section?.sectionName}
        onClick={goBackHeaderOnClick}
      />
      {activeSection.section?.sectionStatus !==
        MatterClientPortalQuestionnaireSectionStatus.Submitted &&
      activeSection.section?.sectionStatus !==
        MatterClientPortalQuestionnaireSectionStatus.Imported ? (
        <ProgressBarComponent
          currentProgress={progress}
          progressText={`${activeSection.section?.sectionName} Section Progress`}
        />
      ) : (
        <></>
      )}
      {!dataSaveError ? (
        <>
          {isSchemaLoading || isUISchemaLoading ? (
            <div></div>
          ) : (
            <JsonformContainer>
              {activeQuestion && isChildQuestion && data.navigation ? (
                <div className='question-breadcrumb'>{data.navigation}</div>
              ) : (
                <></>
              )}
              {getRequiredErrors().length > 0 &&
                validationMode === 'ValidateAndShow' && (
                  <div className='error-header' ref={errorHeaderRef}>
                    <span className='mdi mdi mdi-alert-rhombus'></span>
                    Please fill out all required fields.
                  </div>
                )}

              {activeQuestion && !isChildQuestion && (
                <div className='question-description'>
                  {activeQuestion.Description}
                </div>
              )}
              {activeQuestion && activeQuestion.SubHeader && (
                <div className='question-subheader'>
                  {activeQuestion.SubHeader}
                </div>
              )}

              <JsonFormCustomContext.Provider value={contextValue}>
                <JsonForms
                  schema={schema || {}}
                  uischema={uiSchema || {}}
                  data={data}
                  renderers={formRenderers || []}
                  cells={materialCells}
                  onChange={({ errors, data }) => {
                    handleFormDataChange(data, errors as ErrorObject[])
                  }}
                  validationMode={validationMode}
                  readonly={
                    sectionStatusRef.current ===
                      MatterClientPortalQuestionnaireSectionStatus.Submitted ||
                    sectionStatusRef.current ===
                      MatterClientPortalQuestionnaireSectionStatus.Imported
                  }
                />
              </JsonFormCustomContext.Provider>
            </JsonformContainer>
          )}

          <ButtonsContainer>
            {isChildQuestion ? (
              <>
                <div
                  style={{
                    borderBottom: '1px solid #D1D6D7',
                    marginTop: '-1em',
                    marginBottom: '2em'
                  }}
                />
                {activeSection.section?.sectionStatus !==
                  MatterClientPortalQuestionnaireSectionStatus.Submitted &&
                activeSection.section?.sectionStatus !==
                  MatterClientPortalQuestionnaireSectionStatus.Imported ? (
                  <TwoBtnsContainer>
                    <Button
                      onClick={onChildQuestionBackClick}
                      variant='outline-secondary'
                      data-testid='questionnaire-form-back-button'
                    >
                      Back
                    </Button>

                    <Button
                      onClick={onChildQuestionSaveClick}
                      data-testid='questionnaire-form-continue-button'
                    >
                      Save
                    </Button>
                  </TwoBtnsContainer>
                ) : (
                  <div className='questionnaire-read-only-back-container'>
                    <Button
                      onClick={onChildQuestionBackClick}
                      variant='outline-secondary'
                      data-testid='questionnaire-form-back-button'
                      style={{ width: '100%' }}
                    >
                      Back
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <>
                <div
                  style={{
                    borderBottom: '1px solid #D1D6D7',
                    marginTop: '-2.1em',
                    marginBottom: '2.1em'
                  }}
                />
                <TwoBtnsContainer>
                  <Button
                    onClick={backClick}
                    variant='outline-secondary'
                    data-testid='questionnaire-form-back-button'
                  >
                    Back
                  </Button>
                  {questionType === QUESTION_TYPE.GROUP_NESTED ? (
                    <Button
                      onClick={nestedContinueClick}
                      data-testid='questionnaire-form-continue-button'
                    >
                      Continue
                    </Button>
                  ) : (
                    <Button
                      onClick={continueClick}
                      data-testid='questionnaire-form-continue-button'
                    >
                      Continue
                    </Button>
                  )}
                </TwoBtnsContainer>
              </>
            )}

            {!isChildQuestion &&
            sectionStatusRef.current !=
              MatterClientPortalQuestionnaireSectionStatus.Submitted &&
            sectionStatusRef.current !=
              MatterClientPortalQuestionnaireSectionStatus.Imported ? (
              <div style={{ textAlign: 'center', marginTop: '36px' }}>
                <a href='#' onClick={saveAndFinishLater}>
                  Save and Finish Later
                </a>
              </div>
            ) : (
              <></>
            )}
          </ButtonsContainer>
        </>
      ) : (
        <>Error...</>
      )}
    </>
  )
}

export default QuestionnaireForm
